import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import * as moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { StoreService } from './store.service';

export type CashDirection = 'IN' | 'OUT';

export interface CreateCashMovementPayload {
  store: number;
  terminal: number;
  user: number;
  direction: CashDirection;
  amount: number;
  reason?: string;
}

export interface CashMovementResponse {
  id: number;
  store: number;
  terminal: number;
  user: number;
  direction: CashDirection;
  amount: number;
  reason: string | null;
  created_at: string;
  updated_at?: string;
}

@Injectable()
export class POSService {
  endPoint = environment.apiUrl;
  paymentEndpoint = `${this.endPoint}/backend/payment`;
  validUserPin = false;
  httpOptions = {
    headers: new HttpHeaders({
      "Content-Type": "application/json",
    }),
  };

  public drawerEndLogOutuser: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public posMenuHeight: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public scrolltoTop: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);



  /** The signed-in store's station rows (`pos_setting`), kept for isServerStation() (ENG-5925). */
  private stationRows: any = null;

  constructor(private http: HttpClient, storeService: StoreService) {
    storeService.current.subscribe(store => {
      if (store && store['pos_setting'] !== undefined) { this.stationRows = store['pos_setting']; }
    });
  }

  /**
   * ENG-5925: is this console's station a "server station" (no cash drawer attached)?
   * Reads the station's POS settings row: per-station stores (`multiple_default_load`) match
   * `terminal_id` to the signed-in terminal (localStorage 'terminalId', or the id given),
   * single-row stores use their one row. Such a station never kicks, counts or prompts for a
   * drawer. `store` may be any object carrying `pos_setting`; the current store is the fallback.
   */
  isServerStation(store?: any, terminalId?: string | number | null): boolean {
    const rows = store?.pos_setting !== undefined ? store.pos_setting : this.stationRows;
    if (!rows) { return false; }
    if (Array.isArray(rows)) {
      const id = terminalId ?? localStorage.getItem('terminalId');
      if (id == null || id === '' || id === 'null' || id === 'undefined') { return false; }
      const row = rows.find(r => String(r?.terminal_id) === String(id));
      return !!row?.server_station;
    }
    return !!rows.server_station;
  }

  posAuthorize(terminalId, payload) {
    return this.http.post<any>(
      `${environment.apiUrl}/backend/v2/pos/${terminalId}/authorize/`,
      payload,
      this.httpOptions
    );
  }

  posAuthorizeManual(terminalId, payload, optIn) {
    return this.http.post<any>(
      `${environment.apiUrl}/backend/v2/pos/${terminalId}/authorize/?opt_in=${optIn}&is_open_tab=true`,
      payload,
      this.httpOptions
    );
  }

  posStatus(terminalId, payload) {
    return this.http.post<any>(
      `${environment.apiUrl}/backend/v2/pos/${terminalId}/status/`,
      payload,
      this.httpOptions
    );
  }

  getQuickEntryOrder(storeId) {
    return this.http.get<any>(
      `${environment.apiUrl}/backend/v2/pos/${storeId}/get_quick_order/`,
      this.httpOptions
    );
  }

  openCashDrawer(terminalId) {
    return this.http.get<any>(
      `${environment.apiUrl}/backend/v2/pos/${terminalId}/open_cash_drawer/`,
      this.httpOptions
    );
  }

  posAdjustment(terminalId, orderHash, adjustAmount) {
    var payload = {
      order_hash: orderHash,
      adjust_amount: adjustAmount,
    };
    return this.http.post<any>(
      `${this.endPoint}/backend/v2/pos/${terminalId}/adjust/`,
      payload,
      this.httpOptions
    );
  }

  posRefund(terminalId, orderHash, amount) {
    var payload = {
      order_hash: orderHash,
      amount: amount,
    };
    return this.http.post<any>(
      `${this.endPoint}/backend/v2/pos/${terminalId}/refund/`,
      payload,
      this.httpOptions
    );
  }

  posAddTip(terminalId, orderHash, amount) {
    var payload = {
      order_hash: orderHash,
      amount: amount,
    };
    return this.http.post<any>(
      `${this.endPoint}/backend/v2/pos/${terminalId}/add_tip/`,
      payload,
      this.httpOptions
    );
  }

  getPosLinkLogs(storeId) {
    return this.http.get<any>(`${this.endPoint}/backend/v2/link/logs/${storeId}/`, this.httpOptions);
  }

  /* DRAWER MANAGEMENT API's */
  getManageDrawerStatus(storeId, terminalId, userId) {
    const params = new HttpParams()
    .set('store_id', storeId)
    .set('terminal_id', terminalId)
    .set('user_id', userId)
    return this.http.get<any>(`${this.endPoint}/backend/v2/drawer-manage/${storeId}/`,{ params })
  }

  createDrawer(payload) {
    return this.http.post<any>(`${this.endPoint}/backend/v2/drawer-manage/`,payload, this.httpOptions)
  }

  addDrawerDenominations(storeId, payload) {
    return this.http.post<any>(`${this.endPoint}/backend/v2/drawer-manage/${storeId}/add_denominations/`,payload, this.httpOptions)
  }

  doingPayCashInOut(storeId, payload) {
    return this.http.post<any>(`${this.endPoint}/backend/v2/drawer-manage/${storeId}/cash_pay_in_and_out/`,payload, this.httpOptions)
  }

  endDrawer(storeId, drawerID, payload) {
    return this.http.post<any>(`${this.endPoint}/backend/v2/drawer-manage/${drawerID}/end_drawer/`,payload, this.httpOptions)
  }

  endDrawerDetails(storeId, drawerID) {
    const params = new HttpParams()
    .set('store_id', storeId)
    return this.http.get<any>(`${this.endPoint}/backend/v2/drawer-manage/${drawerID}/end_drawer_detail/`,{ params })

  }

  getCashLogDetails(storeId, terminal_id, start_date, end_date, from, to, page_size, page) {
    let params = new HttpParams()
    .set('terminal_id', terminal_id)
    .set("start_date", moment(start_date).format("YYYY-MM-DD"))
    .set("end_date", moment(end_date).format("YYYY-MM-DD"))
    .set("from_hour", from.hour)
    .set("from_min", from.min)
    .set("to_hour", to.hour)
    .set("to_min", to.min)
    .set('page_size', page_size)
    .set('page', page)
    
    return this.http.get<any>(`${this.endPoint}/backend/v2/drawer-manage/${storeId}/cash_drawer_management_table_data`,{ params })
  }

  getCashMovementDetails(storeId, terminal_id, start_date, end_date, from, to, page_size, page) {
    let params = new HttpParams()
      .set('terminal_id', terminal_id)
      .set("start_date", moment(start_date).format("YYYY-MM-DD"))
      .set("end_date", moment(end_date).format("YYYY-MM-DD"))
      .set("from_hour", from.hour)
      .set("from_min", from.min)
      .set("to_hour", to.hour)
      .set("to_min", to.min)
      .set('page_size', page_size)
      .set('page', page);
  
    return this.http.get<any>(`${this.endPoint}/backend/v2/drawer-manage/${storeId}/cash_movement_table_data`, { params });
  }
  
  cancelBackOrder(orderHash) {
    return this.http.post<any>(`${this.endPoint}/backend/v2/pos/${orderHash}/cancel_back_order/`, this.httpOptions)
  }

  createCashMovement(payload) {
    return this.http.post<any>(`${this.endPoint}/backend/create_cash_movement`,payload, this.httpOptions)
  }

}
